import { Fragment, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import * as graphql from '@treams/treams-network';
import { Card, ChevronDownIcon, ChevronRightIcon, tokens } from '@treams/treams-ui';

const ICON_SIZE = 24;

const List = styled.div`
	> [data-list-item]:last-child {
		border-bottom: 0;
	}
`;

const Item = styled.div<{ $depth: number }>`
	display: flex;
	align-items: center;
	gap: ${tokens.gap.inline}px;
	padding: ${tokens.padding.compact}px ${tokens.padding.control}px;
	padding-left: ${(props) => tokens.padding.control + props.$depth * ICON_SIZE}px;
	border-bottom: 1px solid ${tokens.border.subtle};
	box-sizing: border-box;
	background: transparent;

	&:hover {
		background: ${tokens.surface.hover};
	}
`;

const Toggle = styled.button`
	display: flex;
	flex-shrink: 0;
	align-items: center;
	justify-content: center;
	width: ${ICON_SIZE}px;
	height: ${ICON_SIZE}px;
	padding: 0;
	border: 0;
	background: transparent;
	color: ${tokens.icon.primary};
	cursor: pointer;

	svg {
		display: block;
	}
`;

const IconSlot = styled.div`
	display: flex;
	flex-shrink: 0;
	align-items: center;
	justify-content: center;
	width: ${ICON_SIZE}px;
	height: ${ICON_SIZE}px;
	color: ${tokens.icon.primary};

	svg {
		display: block;
	}
`;

const Name = styled(Link)`
	min-width: 0;
	color: ${tokens.text.primary};
	font-family: ${tokens.typography.fontFamily};
	font-size: ${tokens.typography.sizes.h3}px;
	font-weight: ${tokens.typography.weights.medium};
	line-height: ${tokens.typography.lineHeights.heading};
	text-decoration: underline;
	text-decoration-color: ${tokens.text.secondary};
	text-decoration-thickness: 1px;
	text-underline-offset: 2px;
	text-decoration-skip-ink: none;
`;

function DepartmentRows(props: {
	parentID?: string;
	depth: number;
	groups: Map<string, graphql.Group>;
	edges: Map<string | undefined, string[]>;
	expanded: Set<string>;
	onToggle: (id: string) => void;
}) {
	const children = [...(props.edges.get(props.parentID) ?? [])].sort((a, b) =>
		props.groups.get(a)!.name.localeCompare(props.groups.get(b)!.name),
	);

	return (
		<>
			{children.map((id) => {
				const group = props.groups.get(id)!;
				const nested = props.edges.get(id) ?? [];
				const hasChildren = nested.length > 0;
				const isExpanded = props.expanded.has(id);

				return (
					<Fragment key={id}>
						<Item data-list-item $depth={props.depth}>
							{hasChildren ? (
								<Toggle type="button" aria-expanded={isExpanded} onClick={() => props.onToggle(id)}>
									{isExpanded ? (
										<ChevronDownIcon size={ICON_SIZE} />
									) : (
										<ChevronRightIcon size={ICON_SIZE} />
									)}
								</Toggle>
							) : (
								<IconSlot />
							)}
							<Name to={`/settings/organization/departments/${id}`}>{group.name}</Name>
						</Item>
						{hasChildren && isExpanded && (
							<DepartmentRows
								parentID={id}
								depth={props.depth + 1}
								groups={props.groups}
								edges={props.edges}
								expanded={props.expanded}
								onToggle={props.onToggle}
							/>
						)}
					</Fragment>
				);
			})}
		</>
	);
}

export default function OrganizationDepartmentsTree(props: { groups: graphql.Group[] }) {
	const { groups, edges } = useMemo(() => {
		const groups = new Map(props.groups.map((group) => [group.id, group]));
		const edges = new Map<string | undefined, string[]>();

		for (const group of props.groups) {
			const parentID = group.parent?.id;
			const siblings = edges.get(parentID);

			if (siblings) {
				siblings.push(group.id);
			} else {
				edges.set(parentID, [group.id]);
			}
		}

		return { groups, edges };
	}, [props.groups]);

	const [expanded, setExpanded] = useState(() => new Set(props.groups.map(({ id }) => id)));

	const onToggle = (id: string) => {
		setExpanded((current) => {
			const next = new Set(current);
			const selected = !next.has(id);

			const collapseDescendants = (groupID: string) => {
				if (!selected) {
					next.delete(groupID);
				}

				for (const child of edges.get(groupID) ?? []) {
					collapseDescendants(child);
				}
			};

			if (selected) {
				next.add(id);
			}

			collapseDescendants(id);
			return next;
		});
	};

	return (
		<Card p={0}>
			<List>
				<DepartmentRows
					depth={0}
					groups={groups}
					edges={edges}
					expanded={expanded}
					onToggle={onToggle}
				/>
			</List>
		</Card>
	);
}
